import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaGraduationCap,
  FaFileAlt,
  FaCalculator,
  FaComments,
  FaUserShield,
  FaDownload,
  FaUpload,
  FaQuestionCircle,
  FaEnvelope,
  FaWhatsapp,
  FaInstagram,
  FaTwitter,
  FaLinkedin,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { useFooter } from "../context/FooterContext";

const FAQ = () => {
  const { setShowFooter } = useFooter();
  const [searchTerm, setSearchTerm] = useState("");
  const [openCategories, setOpenCategories] = useState({
    general: true,
    notes: true,
    results: true,
    calculators: true,
    chat: true,
    account: true,
    technical: true,
  });
  const [openQuestions, setOpenQuestions] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(false);

  React.useEffect(() => {
    setShowFooter(true);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  React.useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const toggleCategory = (category) => {
    setOpenCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleQuestion = (questionId) => {
    setOpenQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getBgColor = () => (isDarkMode ? "#0a0a0f" : "#f5f7fb");

  // FAQ Data
  const faqCategories = [
    {
      id: "general",
      name: "General",
      icon: <FaQuestionCircle size={18} />,
      questions: [
        {
          id: "what-is-vtugenx",
          q: "What is VTUGenX?",
          a: "VTUGenX is a comprehensive platform for VTU students to access study materials, notes, previous year question papers, calculate SGPA/CGPA, check results, and connect with fellow students through community chat. It's a one-stop solution for all academic needs.",
        },
        {
          id: "is-free",
          q: "Is VTUGenX free to use?",
          a: "Yes! VTUGenX is completely free for all VTU students. We believe in making quality education accessible to everyone. There are no hidden charges or premium features.",
        },
        {
          id: "how-to-start",
          q: "How do I get started?",
          a: "Simply register with your VTU USN and email, then login to access all features. You can browse notes, check results, use calculators, and join the community chat.",
        },
      ],
    },
    {
      id: "notes",
      name: "Notes & Study Materials",
      icon: <FaFileAlt size={18} />,
      questions: [
        {
          id: "how-upload-notes",
          q: "How can I upload notes?",
          a: "Login to your account, go to Dashboard, click on 'Upload New Notes', fill in the subject details, and select your PDF/DOC/PPT file. Your notes will be reviewed by moderators before being published.",
        },
        {
          id: "note-approval",
          q: "Why are my notes pending approval?",
          a: "All uploaded notes go through a moderation process to ensure quality and relevance. This typically takes 24-48 hours. You'll receive a notification once your notes are approved.",
        },
        {
          id: "supported-formats",
          q: "What file formats are supported?",
          a: "We support PDF, DOC, DOCX, PPT, and PPTX files. Maximum file size is 20MB per upload.",
        },
        {
          id: "download-notes",
          q: "How do I download notes?",
          a: "Browse notes using filters (branch, semester, subject), click on any note card, and use the 'Download' button. You can also preview notes before downloading.",
        },
      ],
    },
    {
      id: "results",
      name: "Results & Rankings",
      icon: <FaGraduationCap size={18} />,
      questions: [
        {
          id: "check-results",
          q: "How to check my results?",
          a: "Go to 'Check Results' page, enter your USN, select exam type (Regular/Revaluation), enter the CAPTCHA, and click 'Fetch Result'. Your result will be displayed with subject-wise marks and SGPA.",
        },
        {
          id: "result-cache",
          q: "How long are results stored?",
          a: "Results are cached for 7 days to reduce server load. After that, they are fetched fresh from VTU servers.",
        },
        {
          id: "ranking-system",
          q: "How are class and university ranks calculated?",
          a: "Ranks are calculated based on your SGPA/percentage compared to other students in your branch (class rank) and across all branches (university rank) for the same semester.",
        },
      ],
    },
    {
      id: "calculators",
      name: "SGPA & CGPA Calculators",
      icon: <FaCalculator size={18} />,
      questions: [
        {
          id: "sgpa-calculation",
          q: "How is SGPA calculated?",
          a: "SGPA = (Σ(Grade Point × Credits)) / Σ(Credits). Grade points: O=10, A+=9, A=8, B+=7, B=6, C=5, P=4, F=0.",
        },
        {
          id: "cgpa-calculation",
          q: "How is CGPA calculated?",
          a: "CGPA = (Σ(SGPA × Credits for that semester)) / Σ(Total Credits). It's weighted by the credits of each semester.",
        },
        {
          id: "save-results",
          q: "Can I save my SGPA/CGPA calculations?",
          a: "Yes! After calculating, your SGPA/CGPA is automatically saved to your academic record. You can view it anytime on your Dashboard.",
        },
      ],
    },
    {
      id: "chat",
      name: "Community Chat",
      icon: <FaComments size={18} />,
      questions: [
        {
          id: "chat-guidelines",
          q: "What are the chat guidelines?",
          a: "Be respectful, no spam, no harassment, no hate speech, no sharing of personal information. Violations may result in warnings, temporary mute, or permanent ban.",
        },
        {
          id: "report-message",
          q: "How to report inappropriate messages?",
          a: "Hover over any message and click the flag icon. Select a reason and submit. Moderators will review and take appropriate action.",
        },
        {
          id: "pin-messages",
          q: "How to pin important messages?",
          a: "Hover over any message and click the pin icon to save it to your personal pins. Admins can also globally pin important announcements.",
        },
      ],
    },
    {
      id: "account",
      name: "Account & Profile",
      icon: <FaUserShield size={18} />,
      questions: [
        {
          id: "update-profile",
          q: "How to update my profile?",
          a: "Go to your Profile page from Dashboard. You can update your name, branch, semester, and profile picture.",
        },
        {
          id: "reset-password",
          q: "How to reset my password?",
          a: "Click 'Forgot Password' on the login page, enter your registered email, and follow the instructions sent to your inbox.",
        },
        {
          id: "delete-account",
          q: "Can I delete my account?",
          a: "Yes, please contact our support team at support@vtugenx.com to request account deletion.",
        },
      ],
    },
    {
      id: "technical",
      name: "Technical Support",
      icon: <FaDownload size={18} />,
      questions: [
        {
          id: "pdf-not-opening",
          q: "Why won't PDFs open?",
          a: "Make sure your browser supports PDF viewing. Try downloading the file instead. If issues persist, clear your browser cache or try a different browser.",
        },
        {
          id: "login-issues",
          q: "Can't login to my account?",
          a: "Check your email/password. Use 'Forgot Password' if needed. Ensure your account is verified via email. Contact support if issues continue.",
        },
        {
          id: "supported-browsers",
          q: "Which browsers are supported?",
          a: "We support Chrome, Firefox, Safari, and Edge (latest versions). For best experience, use Chrome or Firefox.",
        },
      ],
    },
  ];

  // Filter FAQs based on search
  const filteredCategories = faqCategories
    .map((category) => ({
      ...category,
      questions: category.questions.filter(
        (q) =>
          q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.a.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    }))
    .filter((category) => category.questions.length > 0);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: getBgColor(),
        padding: "2rem",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ textAlign: "center", marginBottom: "3rem" }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              width: "70px",
              height: "70px",
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1rem auto",
            }}
          >
            <FaQuestionCircle size={32} color="white" />
          </div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              color: getTextColor(),
              marginBottom: "0.5rem",
            }}
          >
            Frequently Asked <span style={{ color: "#8b5cf6" }}>Questions</span>
          </h1>
          <p
            style={{
              color: getTextSecondary(),
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Find answers to common questions about VTUGenX
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            maxWidth: "600px",
            margin: "0 auto 2rem auto",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              background: getCardBackground(),
              borderRadius: "50px",
              padding: "0.3rem",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <FaSearch
              size={18}
              style={{
                position: "relative",
                left: "1rem",
                top: "0.75rem",
                color: getTextSecondary(),
              }}
            />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                padding: "0.8rem 1rem 0.8rem 2.5rem",
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: "0.9rem",
                color: getTextColor(),
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: getTextSecondary(),
                  padding: "0 1rem",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </motion.div>

        {/* FAQ Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
        >
          {filteredCategories.map((category) => (
            <div
              key={category.id}
              style={{
                background: getCardBackground(),
                borderRadius: "20px",
                border: `1px solid ${getBorderColor()}`,
                overflow: "hidden",
              }}
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                style={{
                  width: "100%",
                  padding: "1rem 1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  borderBottom: openCategories[category.id]
                    ? `1px solid ${getBorderColor()}`
                    : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      background: "rgba(139,92,246,0.1)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#8b5cf6",
                    }}
                  >
                    {category.icon}
                  </div>
                  <h2
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "600",
                      color: getTextColor(),
                      margin: 0,
                    }}
                  >
                    {category.name}
                  </h2>
                  <span
                    style={{
                      background: "rgba(139,92,246,0.15)",
                      color: "#8b5cf6",
                      padding: "0.15rem 0.5rem",
                      borderRadius: "20px",
                      fontSize: "0.7rem",
                    }}
                  >
                    {category.questions.length}
                  </span>
                </div>
                {openCategories[category.id] ? (
                  <FaChevronUp style={{ color: getTextSecondary() }} />
                ) : (
                  <FaChevronDown style={{ color: getTextSecondary() }} />
                )}
              </button>

              {/* Category Questions */}
              {openCategories[category.id] && (
                <div style={{ padding: "0 1.5rem 1.5rem 1.5rem" }}>
                  {category.questions.map((question) => (
                    <div
                      key={question.id}
                      style={{
                        borderBottom: `1px solid ${getBorderColor()}`,
                        marginBottom: "1rem",
                      }}
                    >
                      <button
                        onClick={() => toggleQuestion(question.id)}
                        style={{
                          width: "100%",
                          padding: "1rem 0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "1rem",
                            fontWeight: "500",
                            color: getTextColor(),
                            textAlign: "left",
                          }}
                        >
                          {question.q}
                        </span>
                        {openQuestions[question.id] ? (
                          <FaChevronUp
                            size={14}
                            style={{ color: getTextSecondary() }}
                          />
                        ) : (
                          <FaChevronDown
                            size={14}
                            style={{ color: getTextSecondary() }}
                          />
                        )}
                      </button>
                      {openQuestions[question.id] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          style={{
                            padding: "0 0 1rem 0",
                            color: getTextSecondary(),
                            lineHeight: "1.6",
                            fontSize: "0.9rem",
                          }}
                        >
                          {question.a}
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </motion.div>

        {/* Still Have Questions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: "3rem",
            padding: "2rem",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            borderRadius: "20px",
            textAlign: "center",
            color: "white",
          }}
        >
          <h3 style={{ fontSize: "1.3rem", marginBottom: "0.5rem" }}>
            Still have questions?
          </h3>
          <p style={{ opacity: 0.9, marginBottom: "1.5rem" }}>
            Can't find the answer you're looking for? Please contact our support
            team.
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/contact"
              style={{
                padding: "0.8rem 1.5rem",
                background: "white",
                color: "#8b5cf6",
                textDecoration: "none",
                borderRadius: "10px",
                fontWeight: "500",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaEnvelope /> Contact Us
            </Link>
            <a
              href="https://wa.me/911234567890"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "0.8rem 1.5rem",
                background: "rgba(255,255,255,0.2)",
                color: "white",
                textDecoration: "none",
                borderRadius: "10px",
                fontWeight: "500",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
